import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../core/store/auth/auth.selectors';
import { AuthService } from '../../../core/services/auth.service';
import { SupabaseService } from '../../../core/services/supabase.service';
import { toast } from '../../utils/toast';
import { AppButton } from '../app-button/app-button';
import { PasswordChecklist } from '../password-checklist/password-checklist';
import { passwordPolicyValidator } from '../../../core/auth/password-policy';
import { take } from 'rxjs/operators';
import * as AuthActions from '../../../core/store/auth/auth.actions';
import { UploadService } from '../../../core/services/upload.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, AppButton, PasswordChecklist],
  templateUrl: './user-profile-modal.component.html',
})
export class UserProfileModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private authService = inject(AuthService);
  private supabase = inject(SupabaseService);
  private uploadService = inject(UploadService);
  private dialogRef = inject(DynamicDialogRef);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  userForm: FormGroup;
  userInfo: any;
  saving = false;
  showPassword = false;
  showConfirmPassword = false;
  previewImage: string | null = null;
  selectedFile: File | null = null;

  constructor() {
    this.userForm = this.fb.group({
      name: ['', Validators.required],
      email: [{ value: '', disabled: true }],
      password: ['', passwordPolicyValidator],
      confirmPassword: ['']
    }, { validators: (g: AbstractControl) =>
      (g.get('password')?.value || g.get('confirmPassword')?.value) &&
      g.get('password')?.value !== g.get('confirmPassword')?.value ? { mismatch: true } : null });
  }

  ngOnInit() {
    this.store.select(selectUserInfo).pipe(take(1)).subscribe(userInfo => {
      this.userInfo = userInfo;
      if (userInfo) {
        this.userForm.patchValue({
          name: userInfo.full_name || userInfo.name || userInfo.user_name || '',
          email: userInfo.email
        });
        
        const photoUrl = userInfo.photo || userInfo.photo_url || userInfo.avatar_url;
        if (photoUrl) {
          this.previewImage = photoUrl;
        }
      }
    });

    // Also fetch latest from users_tb to be sure
    if (this.userInfo?.id) {
        this.fetchLatestUserData(this.userInfo.id);
    }
  }

  async fetchLatestUserData(userId: number) {
    try {
        const { data, error } = await this.supabase.client
            .from('users_tb')
            .select('*')
            .eq('id', userId)
            .single();
        
        if (data) {
             this.userInfo = { ...this.userInfo, ...data }; // Merge
             this.userForm.patchValue({
                name: data.name,
                email: data.email
             });
             if (data.photo) {
                 this.previewImage = data.photo;
             }
        }
        if (error) console.error('Error fetching user data', error);

    } catch (e) {
        console.error('Error fetching user data', e);
    }
  }


  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(this.translate.instant('profile_photo_too_large'));
        return;
      }
      this.selectedFile = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        this.previewImage = reader.result as string;
        this.cdr.detectChanges(); // Use detectChanges to ensure update
      };
      reader.readAsDataURL(file);
    }
  }

  async save() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    try {
      const updates: any = {
        name: this.userForm.get('name')?.value
      };

      // 1. Upload Photo if selected (Use UploadService)
      if (this.selectedFile && this.previewImage) {
        try {
           const publicUrl = await firstValueFrom(
               this.uploadService.uploadImage(this.previewImage, 'user', 'profile')
           );
           updates.photo = publicUrl;
        } catch (uploadError) {
           console.error('Upload failed', uploadError);
           toast.error(this.translate.instant('profile_photo_upload_failed'));
           throw uploadError;
        }
      }

      // 2. Update users_tb
      if (this.userInfo?.id) {
          const { error: updateError } = await this.supabase.client
            .from('users_tb')
            .update(updates)
            .eq('id', this.userInfo.id);

          if (updateError) throw updateError;
          
          // Update Store to reflect changes immediately
          this.store.dispatch(AuthActions.updateUserInfo({ 
            userInfo: {
              ...updates,
              full_name: updates.name, // Ensure header updates if it uses full_name
              user_name: updates.name  // Ensure header updates if it uses user_name
            } 
          }));
      }

      // 3. Update Password if provided
      const newPassword = this.userForm.get('password')?.value;
      if (newPassword && newPassword.length > 0) {
        const { error: passError } = await this.authService.updatePassword(newPassword);
        if (passError) throw passError;
      }

      // Success
      toast.success(this.translate.instant('profile_updated_success'));
      
      this.close();

    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error.message || this.translate.instant('profile_update_failed'));
    } finally {
      this.saving = false;
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return field ? (field.invalid && (field.dirty || field.touched)) : false;
  }

  close() {
    this.dialogRef.close();
  }
}
